import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import HeaderBar from './HeaderBar'; 
import UserDrawer from './UserDrawer';
import FloatingChatWidget from '../components/ai-chat/FloatingChatWidget';
import { themeConfig } from './theme';
import { usePageTitle } from '../hooks/usePageTitle';

// ✅ Static styles outside component
const layoutStyles = {
  layoutRoot: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
  },
  header: {
    height: 64,
    flexShrink: 0,
  },
  scrollArea: {
    flex: 1,
    overflow: 'hidden',
    padding: 15,
    background: '#f4f6fa',
  },
  contentBox: {
    height: '100%',
    width: '100%',
    overflowY: 'auto' as const,
    background: '#fff',
    borderRadius: 8,
    padding: 24,
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    border: '1px solid #e5e8ec',
  },
  footer: {
    height: 48,
    flexShrink: 0,
    background: '#fafafa',
    textAlign: 'center' as const,
    lineHeight: '48px',
    borderTop: '1px solid #e5e8ec',
  },
};
interface MainLayoutProps {
  appRoutes: Array<any>;
}

const MainLayout: React.FC<MainLayoutProps> = ({ appRoutes }) => {
  usePageTitle(appRoutes);
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  /* ✅ Determine if sidebar should be hidden based on current path */
  const location = useLocation();

  const noSidebarPaths = appRoutes.filter((route) => route.noSidebar).map((route) => route.path);
  const noSidebar = noSidebarPaths.some((routePath) =>
    location.pathname === routePath ||
    (routePath.includes(':') && location.pathname.startsWith(routePath.split('/:')[0]))
  ); 

  // ✅ Dynamic sidebar width style
  const sidebarStyle = {
    width: collapsed ? 80 : 200,
    transition: 'width 0.3s ease',
  };

  return (
    <ConfigProvider theme={themeConfig}>
      <div style={layoutStyles.layoutRoot}>
        {!noSidebar && (
          <div style={sidebarStyle}>
            <Sidebar
              collapsed={collapsed}
              onToggleSidebar={() => setCollapsed(!collapsed)}
              appRoutes={appRoutes}
            />
          </div>
        )}

        <div style={layoutStyles.mainColumn}>
          <div style={layoutStyles.header}>
            <HeaderBar onDrawerOpen={() => setDrawerOpen(true)} showAppMenu={!noSidebar}/>
          </div>

          <UserDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

          <div style={layoutStyles.scrollArea}>
            <div style={layoutStyles.contentBox}>
              <Outlet />
            </div>
          </div>

          <div style={layoutStyles.footer}>
            © 2025 DeviceHub. All rights reserved.
          </div>
        </div>
      </div>

      {/* Floating Chat Widget */}
      <FloatingChatWidget isOpen={chatOpen} onToggle={() => setChatOpen(!chatOpen)} />
    </ConfigProvider>
  );
};

export default MainLayout;
